import { FUNCTION_BASE } from "../lib/constants";
import { useState, useEffect } from "react";
import { auth, db } from "../lib/firebase";
import { doc, updateDoc, deleteField, getDoc } from "firebase/firestore";
import { updateProfile, updateEmail, updatePassword, EmailAuthProvider, reauthenticateWithCredential } from "firebase/auth";
import { FaInstagram, FaCheckCircle, FaChevronDown, FaChevronUp } from "react-icons/fa";
import { SiMeta } from "react-icons/si";
import "../styles/Settings.css";

const SettingsPage = ({ defaultTab = "account", onConnectFacebook }) => {
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const user = auth.currentUser;

  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [productUpdates, setProductUpdates] = useState(true);

  const [connData, setConnData] = useState(null);
  const [connLoading, setConnLoading] = useState(false);
  const [connExpanded, setConnExpanded] = useState(false);

  useEffect(() => { setActiveTab(defaultTab); }, [defaultTab]);
  useEffect(() => {
    if (user) { setDisplayName(user.displayName || ""); setEmail(user.email || ""); }
  }, [user]);
  useEffect(() => { if (user && !connData) fetchConnData(); }, [user]);

  const fetchConnData = async () => {
    if (!user) return;
    setConnLoading(true);
    try {
      const snap = await getDoc(doc(db, "users", user.uid));
      if (snap.exists()) setConnData(snap.data());
    } catch (e) { console.error(e); }
    setConnLoading(false);
  };

  const handleUpdateProfile = async () => {
    if (!user) return;
    setLoading(true); setError(""); setSuccess("");
    try {
      if (displayName !== user.displayName) await updateProfile(user, { displayName });
      if (email !== user.email) await updateEmail(user, email);
      setSuccess("Profile updated.");
    } catch (err) {
      setError(err.code === "auth/requires-recent-login"
        ? "Please log out and back in to update your email."
        : err.message);
    } finally { setLoading(false); }
  };

  const handleChangePassword = async () => {
    if (!user || !user.email) return;
    setLoading(true); setError(""); setSuccess("");
    if (!currentPassword) { setError("Enter your current password"); setLoading(false); return; }
    if (!newPassword || newPassword.length < 6) { setError("New password must be at least 6 characters"); setLoading(false); return; }
    if (newPassword !== confirmPassword) { setError("Passwords don't match"); setLoading(false); return; }
    try {
      await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, currentPassword));
      await updatePassword(user, newPassword);
      setSuccess("Password changed.");
      setCurrentPassword(""); setNewPassword(""); setConfirmPassword("");
    } catch (err) {
      setError(err.code === "auth/wrong-password" ? "Current password is incorrect" : err.message);
    } finally { setLoading(false); }
  };

  const handleDisconnectFacebook = async () => {
    if (!window.confirm("Disconnect your Facebook account?")) return;
    setLoading(true); setError(""); setSuccess("");
    try {
      await updateDoc(doc(db, "users", user.uid), {
        fbAccessToken: deleteField(), fbAccounts: deleteField(),
        fbPages: deleteField(), fbProfile: deleteField(),
        selectedAdAccount: deleteField(), selectedPageId: deleteField(),
        instagramAccounts: deleteField(),
      });
      setSuccess("Facebook disconnected.");
      setConnData(null);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  if (!user) return <div className="settings-empty">Please log in to view settings.</div>;

  const tabs = [
    { key: "account",       label: "Account" },
    { key: "security",      label: "Security" },
    { key: "notifications", label: "Notifications" },
    { key: "connections",   label: "Connections" },
  ];

  return (
    <div className="settings-page">
      <div className="settings-header">
        <h1 className="settings-title">Settings</h1>
        <p className="settings-subtitle">Manage your account preferences and connected platforms</p>
      </div>

      <div className="settings-tabs">
        {tabs.map(t => (
          <button key={t.key}
            className={`settings-tab${activeTab === t.key ? " active" : ""}`}
            onClick={() => { setActiveTab(t.key); setError(""); setSuccess(""); }}>
            {t.label}
          </button>
        ))}
      </div>

      {success && <div className="settings-success">{success}</div>}
      {error   && <div className="settings-error">{error}</div>}

      {/* ── Account ── */}
      {activeTab === "account" && (
        <div className="settings-card">
          <h2 className="settings-card-title">Profile</h2>
          <p className="settings-card-subtitle">Update your name and email</p>
          <div className="settings-field">
            <label className="settings-label">Full Name</label>
            <input className="settings-input" type="text" value={displayName}
              onChange={e => setDisplayName(e.target.value)} placeholder="John Doe" />
          </div>
          <div className="settings-field">
            <label className="settings-label">Email Address</label>
            <input className="settings-input" type="email" value={email}
              onChange={e => setEmail(e.target.value)} placeholder="you@company.com" />
          </div>
          <button className="settings-btn-primary" onClick={handleUpdateProfile} disabled={loading}>
            {loading ? "Updating..." : "Update Profile"}
          </button>

          <div className="settings-divider" />
          <h2 className="settings-card-title">Account Details</h2>
          <div className="settings-info-compact">
            <div className="settings-info-row">
              <span className="settings-info-label">Email Verified</span>
              <span className="settings-info-value">{user.emailVerified ? "Yes" : "No"}</span>
            </div>
            <div className="settings-info-row">
              <span className="settings-info-label">Account Created</span>
              <span className="settings-info-value">
                {user.metadata.creationTime ? new Date(user.metadata.creationTime).toLocaleDateString() : "—"}
              </span>
            </div>
            <div className="settings-info-row">
              <span className="settings-info-label">Last Sign In</span>
              <span className="settings-info-value">
                {user.metadata.lastSignInTime ? new Date(user.metadata.lastSignInTime).toLocaleDateString() : "—"}
              </span>
            </div>
          </div>

          <div className="settings-divider" />
          <h2 className="settings-card-title">Subscription & API</h2>
          <div className="settings-info-compact">
            <div className="settings-info-row">
              <span className="settings-info-label">Subscription</span>
              <span className="settings-info-value">
                <span className={`settings-status-pill ${connData?.subscriptionStatus === "active" || connData?.subscriptionStatus === "trialing" ? "settings-pill-active" : "settings-pill-inactive"}`}>
                  {connData?.subscriptionStatus === "active" ? "Active"
                    : connData?.subscriptionStatus === "trialing" ? "Trialing"
                    : connData?.subscriptionStatus || "No Subscription"}
                </span>
              </span>
            </div>
            {connData?.currentPeriodEnd && (
              <div className="settings-info-row">
                <span className="settings-info-label">Renews / Expires</span>
                <span className="settings-info-value">
                  {new Date(connData.currentPeriodEnd?.toDate ? connData.currentPeriodEnd.toDate() : connData.currentPeriodEnd).toLocaleDateString()}
                </span>
              </div>
            )}
            <div className="settings-info-row">
              <span className="settings-info-label">Meta Token</span>
              <span className="settings-info-value">
                <span className={`settings-status-pill ${connData?.fbPages?.[0]?.accessToken ? "settings-pill-page" : "settings-pill-user"}`}>
                  {connData?.fbPages?.[0]?.accessToken ? "Page Token" : connData?.fbAccessToken ? "User Token" : "Not Connected"}
                </span>
              </span>
            </div>
          </div>
        </div>
      )}

      {/* ── Security ── */}
      {activeTab === "security" && (
        <div className="settings-card">
          <h2 className="settings-card-title">Change Password</h2>
          <p className="settings-card-subtitle">Use a strong password to keep your account secure</p>
          <div className="settings-field">
            <label className="settings-label">Current Password</label>
            <input className="settings-input" type="password" value={currentPassword}
              onChange={e => setCurrentPassword(e.target.value)} />
          </div>
          <div className="settings-field">
            <label className="settings-label">New Password</label>
            <input className="settings-input" type="password" value={newPassword}
              onChange={e => setNewPassword(e.target.value)} />
          </div>
          <div className="settings-field">
            <label className="settings-label">Confirm New Password</label>
            <input className="settings-input" type="password" value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)} />
          </div>
          <button className="settings-btn-primary" onClick={handleChangePassword} disabled={loading}>
            {loading ? "Changing..." : "Change Password"}
          </button>
        </div>
      )}

      {/* ── Notifications ── */}
      {activeTab === "notifications" && (
        <div className="settings-card">
          <h2 className="settings-card-title">Notifications</h2>
          <p className="settings-card-subtitle">Choose what updates you receive</p>
          <label className="settings-checkbox-row">
            <input className="settings-checkbox" type="checkbox" checked={emailNotifications}
              onChange={e => setEmailNotifications(e.target.checked)} />
            <div>
              <div className="settings-checkbox-title">Email Notifications</div>
              <div className="settings-checkbox-desc">Campaign updates and account alerts</div>
            </div>
          </label>
          <label className="settings-checkbox-row">
            <input className="settings-checkbox" type="checkbox" checked={productUpdates}
              onChange={e => setProductUpdates(e.target.checked)} />
            <div>
              <div className="settings-checkbox-title">Product Updates</div>
              <div className="settings-checkbox-desc">New features and improvements</div>
            </div>
          </label>
        </div>
      )}

      {/* ── Connections ── */}
      {activeTab === "connections" && (
        <div className="conn-cards-grid">
          {connLoading ? <div className="settings-empty">Loading...</div> : (
            <>
              <div className="settings-card conn-simple-card">
                <div className="conn-simple-header">
                  <div className="conn-platform-logo conn-logo-meta">
                    <SiMeta style={{ color: "#0082fb", fontSize: "1.2rem" }} />
                  </div>
                  <div className="conn-platform-info">
                    <div className="conn-platform-name">Meta</div>
                    <div className="conn-platform-sub">Facebook & Instagram</div>
                  </div>
                  {connData?.fbAccessToken
                    ? <span className="conn-status-badge conn-status-connected"><FaCheckCircle style={{ fontSize: "0.6rem" }} /> Connected</span>
                    : <span className="conn-status-badge conn-status-disconnected">Not Connected</span>
                  }
                </div>

                {connData?.fbAccessToken ? (
                  <>
                    <div className="conn-summary-row" onClick={() => setConnExpanded(p => !p)}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        {connData.fbProfile?.picture
                          ? <img src={connData.fbProfile.picture} alt="" className="conn-profile-pic" />
                          : <div className="conn-profile-avatar">{connData.fbProfile?.name?.[0]?.toUpperCase() || "F"}</div>
                        }
                        <div>
                          <div className="conn-profile-name">{connData.fbProfile?.name || "Facebook User"}</div>
                          <div className="conn-simple-counts">
                            {Object.keys(connData.fbAccounts || {}).length} ad accounts
                            {" · "}{(connData.fbPages || []).length} pages
                            {" · "}{(connData.instagramAccounts || []).length} Instagram
                          </div>
                        </div>
                      </div>
                      <span className="conn-expand-icon">{connExpanded ? <FaChevronUp /> : <FaChevronDown />}</span>
                    </div>

                    {connExpanded && (
                      <div className="conn-expanded">
                        {Object.keys(connData.fbAccounts || {}).length > 0 && (
                          <div className="conn-section">
                            <div className="conn-section-label">Ad Accounts</div>
                            <div className="conn-list">
                              {Object.entries(connData.fbAccounts).map(([id, acc]) => (
                                <div key={id} className="conn-list-item">
                                  <div>
                                    <div className="conn-item-name">{acc.name}</div>
                                    <div className="conn-item-sub">{id}</div>
                                  </div>
                                  {connData.selectedAdAccount === id && <span className="conn-active-pill">Active</span>}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {(connData.fbPages || []).length > 0 && (
                          <div className="conn-section">
                            <div className="conn-section-label">Facebook Pages</div>
                            <div className="conn-list">
                              {connData.fbPages.map(page => (
                                <div key={page.id} className="conn-list-item">
                                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                    {page.picture
                                      ? <img src={page.picture} alt="" className="conn-list-pic" />
                                      : <div className="conn-list-avatar conn-list-avatar-fb">{page.name?.[0]?.toUpperCase()}</div>
                                    }
                                    <div>
                                      <div className="conn-item-name">{page.name}</div>
                                      <div className="conn-item-sub">{page.category || "Page"}</div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {(connData.instagramAccounts || []).length > 0 && (
                          <div className="conn-section">
                            <div className="conn-section-label"><FaInstagram style={{ color: "#E4405F", marginRight: 5 }} />Instagram</div>
                            <div className="conn-list">
                              {connData.instagramAccounts.map(acc => (
                                <div key={acc.id} className="conn-list-item">
                                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                    {acc.profile_picture_url
                                      ? <img src={acc.profile_picture_url} alt="" className="conn-list-pic conn-list-pic-round" />
                                      : <div className="conn-list-avatar conn-list-avatar-ig">{acc.username?.[0]?.toUpperCase()}</div>
                                    }
                                    <div>
                                      <div className="conn-item-name">@{acc.username || acc.name}</div>
                                      <div className="conn-item-sub">{(acc.followers_count || 0).toLocaleString()} followers</div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="conn-simple-actions">
                      {onConnectFacebook && <button className="conn-btn-secondary" onClick={onConnectFacebook}>Reconnect</button>}
                      <button className="conn-btn-danger" onClick={handleDisconnectFacebook} disabled={loading}>
                        {loading ? "..." : "Disconnect"}
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="conn-simple-actions" style={{ marginTop: 14 }}>
                    {onConnectFacebook && <button className="settings-btn-primary" onClick={onConnectFacebook}>Connect Meta</button>}
                  </div>
                )}
              </div>

              <div className="settings-card conn-simple-card conn-card-disabled">
                <div className="conn-simple-header">
                  <div className="conn-platform-logo conn-logo-google">
                    <span className="conn-google-g">G</span>
                  </div>
                  <div className="conn-platform-info">
                    <div className="conn-platform-name">Google Ads</div>
                    <div className="conn-platform-sub">Search & Display</div>
                  </div>
                  <span className="conn-status-badge conn-status-soon">Coming Soon</span>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SettingsPage;
